import React, { useEffect, useRef, useState } from "react";
import { GufoIcon } from "@/components/GufoIcon";
import useCountUp from "@/lib/useCountUp";

/**
 * WalletBadge — animated wallet counter with a subtle bump when the value changes.
 * - Count-up animation (easeOutCubic).
 * - Ring pulse + owl wiggle on increase; softer red glow on decrease.
 */
export default function WalletBadge({ amount = 0 }) {
  const display = useCountUp(amount, 750);
  const prevRef = useRef(amount);
  const [flash, setFlash] = useState(null); // "up" | "down" | null

  useEffect(() => {
    const prev = prevRef.current;
    if (amount > prev) setFlash("up");
    else if (amount < prev) setFlash("down");
    prevRef.current = amount;
    if (flash) {
      const t = setTimeout(() => setFlash(null), 900);
      return () => clearTimeout(t);
    }
  }, [amount]); // eslint-disable-line

  const flashStyle =
    flash === "up" ? { boxShadow: "0 0 0 3px rgba(34,197,94,0.35), 0 8px 24px -8px rgba(34,197,94,0.4)" } :
    flash === "down" ? { boxShadow: "0 0 0 3px rgba(239,68,68,0.35), 0 8px 24px -8px rgba(239,68,68,0.4)" } :
    undefined;

  return (
    <div
      className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass glow-brand transition-all duration-500"
      data-testid="wallet-badge"
      style={flashStyle}
    >
      <span className={flash === "up" ? "wiggle inline-flex" : "inline-flex"}>
        <GufoIcon size={22} />
      </span>
      <span className="display font-black text-lg leading-none tabular-nums" style={{ color: "var(--brand)" }}>
        {display}
      </span>
      <span className="text-xs uppercase tracking-widest text-slate-500">Gufi</span>
    </div>
  );
}
